import React, { useState } from "react";
import {
  Col,
  Row,
  Typography,
  Button,
  Modal,
  Table,
  Input,
  Space,
  Image,
  notification,
  Grid,
  Tag,
  Select,
  Spin,
} from "antd";
import {
  ExclamationCircleFilled,
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { AwardModal } from "@components";
import { useDebounce } from "@hooks";
import { awardsAPI } from "@api";
import { fallbackImage } from "@assets";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { OrderByDirection } from "@constants";
import { AwardsMobile } from "./AwardsMobile";

const { confirm } = Modal;
const { Search } = Input;
const { useBreakpoint } = Grid;

const OrderByField = {
  name: 'name',
  description: 'description',
  amount: 'amount',
  available: 'available',
}

const AwardsTable = () => {
  const [params, setParams] = useState({
    page: 1,
    limit: 20,
    orderByField: null,
    orderByDirection: null,
    available: null,
  });
  const [showAwardModal, setShowAwardModal] = useState(false);
  const [currentAward, setCurrentAward] = useState(null);
  const [searchAward, setSearchAward] = useState("");
  const debouncedSearchAward = useDebounce(searchAward, 500);
  const queryClient = useQueryClient();
  const { t } = useTranslation("common");
  const { t: tError } = useTranslation("error");
  const { md, lg } = useBreakpoint();

  const { isLoading, data: awards } = useQuery({
    queryKey: ["awards", { ...params, search: debouncedSearchAward }],
    queryFn: () => awardsAPI.getAwards({ ...params, search: debouncedSearchAward }).then(({ data }) => data),
    onError: ({ response }) =>
      notification.error({
        message: tError(response.data.message),
      }),
  });

  const { mutate: deleteAwardMutation } = useMutation({
    mutationFn: awardsAPI.deleteAward,
    onSuccess: () => queryClient.invalidateQueries(["awards"]),
    onError: ({ response }) =>
      notification.error({
        message: tError(response.data.message),
      }),
  });

  const columns = [
    {
      key: "name",
      title: t("awardTable.name"),
      dataIndex: "name",
      fixed: lg ? "left" : "",
      width: 300,
      sorter: true,
    },
    {
      key: "description",
      title: t("awardTable.description"),
      dataIndex: "description",
      width: 300,
      sorter: true,
    },
    {
      title: t("awardTable.imageUrl"),
      dataIndex: "imageUrl",
      align: "center",
      width: 110,
      render: (_, award) => (
        <Image
          width={50}
          height={50}
          src={award.imageUrl}
          alt={award.name}
          fallback={fallbackImage}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
          wrapperStyle={{
            width: "50px",
            height: "50px",
            overflow: "hidden",
            borderRadius: "5px",
          }}
        />
      ),
    },
    {
      key: "amount",
      title: t("awardTable.amount"),
      dataIndex: "amount",
      align: "center",
      width: 80,
      sorter: true,
      render: (amounts, award) => amounts.map(amount => <Tag key={award.id} style={{ margin: '8px' }}> {amount} </Tag>)
    },
    {
      key: "available",
      title: t("awardTable.available"),
      dataIndex: "available",
      align: "center",
      width: 80,
      sorter: true,
      render: (_, { available }) =>
        available ? <Tag color="success">{t("awardTable.available")}</Tag> : <Tag color="error">{t("awardTable.unavailable")}</Tag>,
    },
    {
      dataIndex: "id",
      title: t("awardTable.actions"),
      fixed: md ? "right" : "",
      align: "center",
      width: 100,
      render: (_, award) => (
        <Space>
          <Button
            onClick={() => {
              setCurrentAward(award);
              setShowAwardModal(true);
            }}
          >
            <EditOutlined />
          </Button>
          <Button onClick={() => showDeactivateAwardConfirm(award)} danger>
            <DeleteOutlined />
          </Button>
        </Space>
      ),
    },
  ];

  const showDeactivateAwardConfirm = (award) => {
    confirm({
      title: t("awardTable.delete.confirm"),
      icon: <ExclamationCircleFilled />,
      content: t("awardTable.delete.description"),
      okType: "danger",
      okText: t("awardTable.delete.ok"),
      cancelText: t("awardTable.delete.cancel"),
      maskClosable: true,
      centered: true,
      onOk: () => deleteAwardMutation(award.id),
    });
  };

  return (
    <>
      <Row justify="center" align="middle" style={{ maxWidth: 1300, margin: "0px auto", padding: 10, background: "white" }}>
        <Col span={22}>
          <Typography.Title align="left" level={2} style={{ marginTop: 0 }}>
            {t("navbar.awards")}
          </Typography.Title>
        </Col>
        <Col
          span={22}
          align="end"
          style={{
            borderTopLeftRadius: "5px",
            borderTopRightRadius: "5px",
            overflow: "hidden",
            width: "100%",
            padding: "16px 0px",
            display: "flex",
            justifyContent: md ? "space-between" : "center",
            columnGap: "8px",
            rowGap: "10px",
            flexWrap: "wrap",
            background: "#fff",
          }}
        >
          <Space direction={md ? "horizontal" : "vertical"} style={{ width: md ? 'auto' : '100%' }} >
            <Search
              onChange={(e) => setSearchAward(e.target.value)}
              style={{ width: "100%", maxWidth: "400px" }}
              allowClear
              placeholder={t("awardTable.search")}
            />
            <Select
              onChange={(value) => setParams({
                ...params,
                available: value
              })}
              style={{
                width: md ? "200px" : "100%",
                textAlign: "left"
              }}
              placeholder={t("awardTable.available")}
              options={[
                {
                  value: true,
                  label: t("awardTable.available")
                },
                {
                  value: false,
                  label: t("awardTable.unavailable")
                }
              ]}
              allowClear
            />
          </Space>

          {
            md &&
            <Button
              icon={<PlusOutlined />}
              onClick={() => setShowAwardModal(true)}
            >
              {t("awardTable.add")}
            </Button>
          }
        </Col>

        {
          md ?
            <Col
              span={22}
              align="center"
              style={{
                height: "100%",
                backgroundColor: "#fff",
                borderRadius: "5px",
              }}
            >
              <Table
                dataSource={awards?.data ?? []}
                columns={columns}
                pagination={{
                  current: params.page,
                  pageSize: params.limit,
                  total: awards?.count,
                  position: ["bottomCenter"],
                  size: "default"
                }}
                rowKey={"id"}
                scroll={{ x: "max-content", y: "calc(100vh - 335px)" }}
                size="small"
                loading={isLoading}
                borderRadius={false}
                onChange={(pagination, filters, sorter) => {
                  setParams({
                    ...params,
                    page: pagination.current,
                    limit: pagination.pageSize,
                    orderByField: OrderByField[sorter.columnKey],
                    orderByDirection: OrderByDirection[sorter.order]
                  })
                }}
              />
            </Col>
            :
            <Spin spinning={isLoading} >
              <AwardsMobile awards={awards} />
            </Spin>
        }
        <AwardModal
          award={currentAward}
          open={showAwardModal}
          onCancel={() => {
            setShowAwardModal(false);
            setCurrentAward(null);
          }}
        />
      </Row>
    </>
  );
};

export default AwardsTable;
